import type { PepLocale } from "../../i18n/types";

interface ProductVisualShowcaseProps {
  locale: PepLocale;
  /** Real product screenshot URL (static public asset). */
  productImageSrc: string;
  productImageAlt: string;
  /** Optional human cutout overlaid on the product shot. */
  humanImageSrc?: string;
  humanImageAlt?: string;
  /** Accessible caption — product-specific when provided. */
  caption?: string;
}

/**
 * Premium frame for a real product screenshot, with optional human foreground.
 * Does not recreate UI in HTML/CSS.
 */
export function ProductVisualShowcase({
  locale,
  productImageSrc,
  productImageAlt,
  humanImageSrc,
  humanImageAlt,
  caption,
}: ProductVisualShowcaseProps) {
  const figCaption =
    caption ??
    (locale === "en"
      ? "ITaaS Suite product interface"
      : "Interface produit ITaaS Suite");

  return (
    <figure className={`pep-pd-visual pep-pd-visual--shot${humanImageSrc ? " pep-pd-visual--with-human" : ""}`}>
      <div className="pep-pd-visual__stage">
        <div className="pep-pd-visual__frame">
          <img
            className="pep-pd-visual__shot"
            src={productImageSrc}
            alt={productImageAlt}
            width={1280}
            height={800}
            loading="eager"
            decoding="async"
          />
        </div>
        {humanImageSrc ? (
          <img
            className="pep-pd-visual__human"
            src={humanImageSrc}
            alt={
              humanImageAlt ??
              (locale === "en"
                ? "Professionals collaborating on a laptop"
                : "Professionnels collaborant sur un ordinateur portable")
            }
            width={640}
            height={480}
            loading="eager"
            decoding="async"
          />
        ) : null}
      </div>
      <figcaption className="visually-hidden">{figCaption}</figcaption>
    </figure>
  );
}
